import * as React from "react"
import { type LegalEntityBadgeCount } from "@digits-graphql/frontend/graphql-bearer"
import { useActionItemsBadgeCountsQuery } from "src/frontend/components/OS/Applications/hooks/useActionItemsBadgeCountsQuery"
import { useBoostBadgeCountsQuery } from "src/frontend/components/OS/Applications/hooks/useBoostBadgeCountsQuery"
import { useTransactionReviewBadgeCountsQuery } from "src/frontend/components/OS/Applications/hooks/useTransactionReviewBadgeCountsQuery"

export enum BadgeName {
  ActionItems,
  TransactionReview,
  QualityCheck,
}

interface BadgeContextValue {
  loading: boolean
  badgeCount: (leId: string, badge: BadgeName) => number
  hasBadge: (leId: string, badge: BadgeName) => boolean
}

const defaultValue = {
  loading: false,
  badgeCount: () => 0,
  hasBadge: () => false,
}

const BadgeContext = React.createContext<BadgeContextValue>(defaultValue)

interface Props {
  skipActionItems?: boolean
  poll?: boolean
}

export const BadgeContextProvider: React.FC<React.PropsWithChildren<Props>> = ({
  skipActionItems = false,
  poll = false,
  children,
}) => {
  // only fetch all badge counts for ActionItems since this count is displayed on the legal entity selector
  // otherwise only fetch badge counts for the current legal entity
  const { loading: actionItemsLoading, counts: actionItemsCounts } = useActionItemsBadgeCountsQuery(
    poll,
    skipActionItems
  )

  const { loading: boostLoading, counts: boostCounts } = useBoostBadgeCountsQuery()

  const { loading: reviewLoading, counts: reviewCounts } = useTransactionReviewBadgeCountsQuery()

  const counts = React.useMemo(() => {
    const legalEntityIds = [
      ...(actionItemsCounts || []),
      ...(boostCounts || []),
      ...(reviewCounts || []),
    ].map(({ legalEntityId }) => legalEntityId)

    return new Map(
      legalEntityIds?.map((le) => {
        const actionItemsCount = findCount(le, actionItemsCounts)
        const boostCount = findCount(le, boostCounts)
        const reviewCount = findCount(le, reviewCounts)
        return [
          le,
          new Map([
            [BadgeName.ActionItems, actionItemsCount],
            [BadgeName.QualityCheck, boostCount],
            [BadgeName.TransactionReview, reviewCount],
          ]),
        ]
      })
    )
  }, [actionItemsCounts, boostCounts, reviewCounts])

  const loading = boostLoading || reviewLoading || actionItemsLoading

  const value = React.useMemo<BadgeContextValue>(
    () => ({
      loading,
      badgeCount: (leId: string, badge: BadgeName) => counts.get(leId)?.get(badge) ?? 0,
      hasBadge: (leId: string, badge: BadgeName) => !!counts.get(leId)?.get(badge),
    }),
    [counts, loading]
  )

  return <BadgeContext.Provider value={value}>{children}</BadgeContext.Provider>
}

const findCount = (le: string, badgeCounts?: LegalEntityBadgeCount[]) =>
  badgeCounts?.find(({ legalEntityId }) => le === legalEntityId)?.count ?? 0

export function useBadgeContext() {
  return React.useContext(BadgeContext)
}
